import { useState } from "react";
import { useTranslation } from "react-i18next";

import { IUserPreferences } from "../../../../@types/Preferences";
import { Dropdown, DropdownTextItem } from "../../../../components/Dropdown";
import { AUTH_UNIT_PREFERENCES } from "../../../../constants/localStorageKeys";
import { useLanguages } from "../../../../hooks/queries/languages";
import { useToast } from "../../../../hooks/useToast";
import { PageTitle } from "../../../../layout/components/PageTitle";
import { userService } from "../../../../services/userService";
import { safeJsonParse, safeStorage } from "../../../../utils/storage";
import {
  LanguageContainer,
  LanguageRow,
  LanguageTitle,
  SectionContainer,
} from "./LanguageSection.styles";

/**
 * EN-5191: lets an HCP choose the portal's language.
 *
 * Options come from the platform's language registry filtered to the ones with portal
 * translations (see useLanguages), never a list in this file: a language the API cannot serve
 * would render the English fallback and read as a broken setting.
 */
export function LanguageSection() {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const { languages } = useLanguages();

  const [isSaving, setIsSaving] = useState(false);

  const currentLanguage = i18n.language;
  const currentName =
    languages.find((language) => language.code === currentLanguage)?.name ??
    currentLanguage;

  const handleLanguageChange = async (selected: string) => {
    if (!selected || selected === currentLanguage || isSaving) return;

    setIsSaving(true);
    const previousLanguage = currentLanguage;

    try {
      const result = await userService.updateUserLanguage(selected);

      if (result.status < 200 || result.status >= 300) {
        showToast(t("settings_language_saveFailed"), "error");
        return;
      }

      // Kept in step with the stored preferences, which is what the language detector reads on
      // the next page load — otherwise a refresh would undo the change just made.
      const stored = safeJsonParse<IUserPreferences | null>(
        safeStorage.getItem(AUTH_UNIT_PREFERENCES),
        null
      );

      if (stored) {
        safeStorage.setItem(
          AUTH_UNIT_PREFERENCES,
          JSON.stringify({ ...stored, languageCode: selected })
        );
      }

      // Applied last, and only once the write succeeded: switching first would leave the portal
      // showing a language the server did not accept.
      await i18n.changeLanguage(selected);

      showToast(t("settings_language_saved"), "success");
    } catch {
      await i18n.changeLanguage(previousLanguage);
      showToast(t("settings_language_saveFailed"), "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SectionContainer>
      <PageTitle>{t("settings_language_title")}</PageTitle>

      <LanguageContainer>
        <LanguageRow>
          <LanguageTitle>{t("settings_language_label")}</LanguageTitle>

          <Dropdown
            value={currentLanguage}
            trigger={currentName}
            onValueChange={handleLanguageChange}
          >
            {languages.map((language) => (
              <DropdownTextItem
                key={language.id ?? language.code}
                // The language's own name, left untranslated on purpose: a picker that renders
                // every option in the language you are leaving is unusable for the person who
                // cannot read it.
                text={language.name || language.code}
                // Always set. DropdownTextItem emits `value || text`, so without it the handler
                // would receive the display name and try to save "Dutch" as a language code.
                value={language.code}
              />
            ))}
          </Dropdown>
        </LanguageRow>
      </LanguageContainer>
    </SectionContainer>
  );
}
